import { useState } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Sparkles, Loader2, CheckCircle, AlertCircle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = `${window.location.origin}/api`;

// TikTok icon (not in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

export default function SocialProofVerification({ businessId, existingData, onVerified }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    instagram_handle: '',
    tiktok_handle: '',
    instagram_bio: '',
    tiktok_bio: ''
  });
  const [showBioFields, setShowBioFields] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.instagram_handle && !formData.tiktok_handle && !formData.instagram_bio && !formData.tiktok_bio) {
      toast.error('Please provide at least one social media handle or bio');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API}/businesses/${businessId}/verify-social`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }

      setResult(data);
      
      if (data.confidence_score >= 50) {
        toast.success(`Great! Confidence score: ${data.confidence_score}%`);
        onVerified?.(data.confidence_score);
      } else if (data.confidence_score > 0) {
        toast.info(`Partial match found. Score: ${data.confidence_score}%`);
      } else {
        toast.warning('No Black-owned indicators found. Try adding your bio text directly.');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreLabel = (score) => {
    if (score >= 70) return 'High Confidence';
    if (score >= 40) return 'Medium Confidence';
    if (score > 0) return 'Low Confidence';
    return 'Not Verified';
  };

  return (
    <>
      <button
        onClick={() => setDialogOpen(true)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/20 border border-purple-500/30 text-purple-400 hover:bg-purple-500/30 transition-colors"
        data-testid="social-proof-btn"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-medium">
          {existingData?.confidence_score > 0 
            ? `Score: ${existingData.confidence_score}%` 
            : 'Verify Social Proof'}
        </span>
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Social Proof Verification
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              We&apos;ll analyze your social media profiles for Black-owned business indicators
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5 py-4">
            {/* Existing Score Display */}
            {existingData?.confidence_score > 0 && (
              <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm">Current Confidence Score</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-2xl font-bold ${getScoreColor(existingData.confidence_score)}`}>
                      {existingData.confidence_score}%
                    </span>
                    <span className={`text-xs ${getScoreColor(existingData.confidence_score)}`}>
                      {getScoreLabel(existingData.confidence_score)}
                    </span>
                  </div>
                </div>
                {existingData.keywords_found?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {existingData.keywords_found.map((kw, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs">
                        {kw}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Instagram */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm flex items-center gap-2">
                <Instagram className="w-4 h-4" />
                Instagram Handle
              </Label>
              <Input
                value={formData.instagram_handle}
                onChange={(e) => setFormData({ ...formData, instagram_handle: e.target.value })}
                placeholder="@yourbusiness"
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="instagram-handle-input"
              />
            </div>

            {/* TikTok */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm flex items-center gap-2">
                <TikTokIcon className="w-4 h-4" />
                TikTok Handle
              </Label>
              <Input
                value={formData.tiktok_handle}
                onChange={(e) => setFormData({ ...formData, tiktok_handle: e.target.value })}
                placeholder="@yourbusiness"
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="tiktok-handle-input"
              />
            </div>

            {/* Toggle for Bio Fields */}
            <button
              type="button"
              onClick={() => setShowBioFields(!showBioFields)}
              className="text-purple-400 text-sm hover:text-purple-300 transition-colors"
            >
              {showBioFields ? '− Hide bio fields' : '+ Add bio text directly (recommended for better accuracy)'}
            </button>

            {/* Bio Text Fields */}
            {showBioFields && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-4"
              >
                <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <p className="text-purple-400/80 text-xs">
                    Paste your actual bio text for more accurate analysis. 
                    We look for keywords like &quot;Black Owned&quot;, &quot;BOP&quot;, cultural references, etc.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">Instagram Bio</Label>
                  <Textarea
                    value={formData.instagram_bio}
                    onChange={(e) => setFormData({ ...formData, instagram_bio: e.target.value })}
                    placeholder="Paste your Instagram bio here..."
                    className="bg-zinc-900 border-zinc-800 text-white min-h-[80px]"
                    data-testid="instagram-bio-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">TikTok Bio</Label>
                  <Textarea
                    value={formData.tiktok_bio}
                    onChange={(e) => setFormData({ ...formData, tiktok_bio: e.target.value })}
                    placeholder="Paste your TikTok bio here..."
                    className="bg-zinc-900 border-zinc-800 text-white min-h-[80px]"
                    data-testid="tiktok-bio-input"
                  />
                </div>
              </motion.div>
            )}

            {/* Keywords we look for */}
            <div className="p-3 rounded-lg bg-zinc-900/30 border border-zinc-800">
              <p className="text-zinc-500 text-xs mb-2">Keywords we look for:</p>
              <div className="flex flex-wrap gap-1">
                {['Black Owned', 'BOP', 'Melanin', 'Buy Black', 'Black Excellence', '✊🏾', '✊🏿'].map((kw) => (
                  <span key={kw} className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 text-xs">
                    {kw}
                  </span>
                ))}
                <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-500 text-xs">+more</span>
              </div>
            </div>

            {/* Result Display */}
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-medium">Analysis Result</span>
                  <div className="flex items-center gap-2">
                    <TrendingUp className={`w-4 h-4 ${getScoreColor(result.confidence_score)}`} />
                    <span className={`text-xl font-bold ${getScoreColor(result.confidence_score)}`}>
                      {result.confidence_score}%
                    </span>
                  </div>
                </div>

                {result.social_proof?.keywords_found?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-zinc-500 text-xs mb-1">Keywords found:</p>
                    <div className="flex flex-wrap gap-1">
                      {result.social_proof.keywords_found.map((kw, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <p className={`text-sm ${getScoreColor(result.confidence_score)}`}>
                  {getScoreLabel(result.confidence_score)}
                  {result.confidence_score >= 50 && ' - Great match!'}
                </p>
              </motion.div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold hover:from-purple-500 hover:to-pink-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="analyze-social-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Analyze Social Proof
                </>
              )}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
